import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { GeminiModelOption } from '../hooks/useUserModelSelection';

interface CustomModelSelectorProps {
  models: GeminiModelOption[];
  isLoading: boolean;
  selectedModelId: string;
  isSaving: boolean;
  onSelect: (modelId: string) => void;
}

const CustomModelSelector = ({ models, isLoading, selectedModelId, isSaving, onSelect }: CustomModelSelectorProps) => {
  // Normalize the value to prevent MUI Select out-of-range warnings
  // Note: selectedModelId actually contains the model NAME (not ID) because backend expects model_name
  const effectiveValue = useMemo(() => {
    return models.some((m) => m.name === selectedModelId) ? selectedModelId : '';
  }, [models, selectedModelId]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onSelect(event.target.value);
  };

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'neutral.otherBorder',
        boxShadow: 'none',
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Choose a custom model
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Pick one of the available Gemini models to personalize your experience.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You can change this selection at any time.
              </Typography>
            </Stack>
          </Box>

          <FormControl fullWidth disabled={isLoading || isSaving}>
            <InputLabel id="custom-model-select-label" shrink>
              Custom model
            </InputLabel>
            <Select
              labelId="custom-model-select-label"
              value={effectiveValue}
              label="Custom model"
              onChange={handleChange}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Use the Default Model
                </Typography>
              </MenuItem>
              {models.map((model) => (
                <MenuItem key={model.id} value={model.name}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {model.name}
                    </Typography>
                    {model.description ? (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {model.description}
                      </Typography>
                    ) : null}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(isLoading || isSaving) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {isLoading ? 'Loading available models…' : 'Saving your selection…'}
              </Typography>
            </Box>
          )}

          {!isLoading && models.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No custom models are currently available. Please check back later.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CustomModelSelector;
